import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SectionHeader from '@/components/feature/SectionHeader';
import MovieCard from '@/components/feature/MovieCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, getMoviesByCategory } from '@/mocks/movies';

export default function Category() {
  const { slug = 'all' } = useParams<{ slug: string }>();

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug) || categories[0],
    [slug]
  );

  const filteredMovies = useMemo(() => getMoviesByCategory(slug), [slug]);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: currentCategory.name, to: `/category/${slug}` },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="BreadcrumbList" />

      <main className="pt-20 md:pt-24">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑导航">
              {breadcrumbItems.map((item, index) => (
                <span key={item.to} className="flex items-center gap-1.5">
                  {index > 0 && <i className="ri-arrow-right-s-line text-sm"></i>}
                  {index === breadcrumbItems.length - 1 ? (
                    <span className="text-foreground-200">{item.label}</span>
                  ) : (
                    <Link to={item.to} className="hover:text-primary-500 transition-colors duration-200">
                      {item.label}
                    </Link>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </div>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 pb-6">
          <div className="max-w-7xl mx-auto">
            <div className="inline-flex items-center gap-1 p-1 bg-background-100 rounded-full">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-400 hover:text-foreground-100'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Category Title */}
        <section className="w-full px-4 md:px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
              {currentCategory.name}
              <span className="ml-2 text-sm font-normal text-foreground-500">
                共{filteredMovies.length}部
              </span>
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              88影视网为您精选{currentCategory.name}类高清影视资源，高清不卡顿，畅享极致观影体验。
            </p>
          </div>
        </section>

        {/* Movie Grid */}
        <section className="w-full px-4 md:px-6 pb-16">
          <div className="max-w-7xl mx-auto">
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 mb-4">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请查看其他分类</p>
                <Link
                  to="/category/all"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  查看全部影视
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}